import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCollaborations } from "../hooks/useCollaborations";
import { AlertCircle, Eye, X, Zap, Power, PowerOff, Store, MessageSquare } from "lucide-react";
import { makePublicEntity, usePublicEntities } from "../../../../hooks/usePublicEntities";
import { chatLink } from "../utils/chatLink";

const CONFLICT_REASONS = {
  SOURCE_ALREADY_COVERED: "la boutique est déjà affectée entièrement à une autre collaboration",
  SOURCE_HAS_PRODUCT_ASSIGNMENTS: "des produits de cette boutique sont déjà affectés à une autre collaboration",
  SOURCE_ALREADY_ASSIGNED_COMPLETELY: "la boutique est déjà affectée entièrement à une autre collaboration",
  PRODUCT_ALREADY_ASSIGNED: "ce produit est déjà affecté à une autre collaboration",
};

function apiErrorMessage(error, fallback) {
  const data = error?.response?.data || {};
  const reasons = [...new Set((data.conflicts || []).map((c) => CONFLICT_REASONS[c.reason]).filter(Boolean))];
  const base = data.detail || fallback;
  return reasons.length ? `${base} (${reasons.join(" ; ")})` : base;
}

function StatusBadge({ status }) {
  const styles = {
    pending: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/30",
    active: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30",
    inactive: "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700",
    rejected: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/30",
    countered: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-500/10 dark:text-violet-400 dark:border-violet-500/30",
    cancelled: "bg-slate-50 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:border-slate-700",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${styles[status] || styles.inactive}`}>
      {status}
    </span>
  );
}

function RespondModal({ open, onClose, onRespond, collaboration }) {
  const [action, setAction] = useState("accept");
  const [price, setPrice] = useState("");
  const [message, setMessage] = useState("");

  if (!open || !collaboration) return null;

  const handleSubmit = () => {
    const data = { action, message };
    if (action === "counter") {
      data.price_finale = parseFloat(price);
    }
    onRespond(collaboration.id, data);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 dark:bg-black/60">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 dark:bg-slate-900">
        <div className="p-5 border-b border-slate-200 dark:border-slate-800">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Respond to Collaboration</h2>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex gap-2">
            {["accept", "reject", "counter"].map((a) => (
              <button
                key={a}
                onClick={() => setAction(a)}
                className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium capitalize ${
                  action === a
                    ? "bg-indigo-600 text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                }`}
              >
                {a}
              </button>
            ))}
          </div>
          {action === "counter" && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-300">Counter Price</label>
              <input
                type="number"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:outline-none dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 dark:focus:ring-indigo-500/30"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-300">Message</label>
            <textarea
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:outline-none dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 dark:focus:ring-indigo-500/30"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700"
            >
              Submit
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ThreadModal({ open, onClose, thread }) {
  if (!open || !thread) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 dark:bg-black/60">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto dark:bg-slate-900">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-800">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Negotiation Thread</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
            <X size={20} className="text-slate-500 dark:text-slate-400" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {thread?.items?.map((item) => (
            <div key={item.id} className="p-4 rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60">
              <div className="flex items-center justify-between mb-2">
                <StatusBadge status={item.status} />
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  {item.kind} - {new Date(item.created_at).toLocaleString()}
                </span>
              </div>
              <p className="text-sm font-medium dark:text-slate-100">Price: {item.price_finale} {item.currency}</p>
              {item.message && <p className="text-sm text-slate-600 mt-1 dark:text-slate-400">{item.message}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminMarketplaceCollaborations() {
  const { collaborations, loading, error, fetchCollaborations, fetchSummary, respond, deactivate, activate, fetchThread, summary } = useCollaborations();
  const [statusFilter, setStatusFilter] = useState("");
  const [respondModal, setRespondModal] = useState({ open: false, collaboration: null });
  const [threadModal, setThreadModal] = useState({ open: false, thread: null });
  const [actionLoading, setActionLoading] = useState(null);
  const [actionError, setActionError] = useState("");
  const navigate = useNavigate();
  const providers = useMemo(
    () => collaborations.map((c) => makePublicEntity(c.provider_type, c.provider_id)).filter(Boolean),
    [collaborations]
  );
  const { getEntity } = usePublicEntities(providers);
  const providerName = (collab) =>
    getEntity(collab.provider_type, collab.provider_id)?.display_name ||
    (collab.provider_type === "AGENCY_OWNER" ? "Agence" : "Agent");

  useEffect(() => {
    fetchCollaborations();
    fetchSummary();
  }, [fetchCollaborations, fetchSummary]);

  const filtered = statusFilter ? collaborations.filter((c) => c.status === statusFilter) : collaborations;

  const handleRespond = async (id, data) => {
    setActionLoading(id);
    setActionError("");
    try {
      await respond(id, data);
    } catch (e) {
      setActionError(apiErrorMessage(e, "Réponse impossible."));
    }
    setActionLoading(null);
  };

  const handleDeactivate = async (id) => {
    setActionLoading(id);
    setActionError("");
    try {
      await deactivate(id);
    } catch (e) {
      setActionError(apiErrorMessage(e, "Désactivation impossible."));
    }
    setActionLoading(null);
  };

  const handleActivate = async (id) => {
    setActionLoading(id);
    setActionError("");
    try {
      await activate(id);
    } catch (e) {
      setActionError(apiErrorMessage(e, "Activation impossible."));
    }
    setActionLoading(null);
  };

  const handleViewThread = async (id) => {
    const thread = await fetchThread(id);
    setThreadModal({ open: true, thread });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin dark:border-indigo-500/20 dark:border-t-indigo-400" />
          <p className="text-sm text-slate-500 dark:text-slate-500">Loading collaborations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-center dark:bg-rose-500/10 dark:border-rose-500/30">
        <AlertCircle size={32} className="mx-auto text-rose-500 mb-2 dark:text-rose-400" />
        <p className="text-rose-700 font-medium dark:text-rose-400">Failed to load collaborations</p>
        <button onClick={() => fetchCollaborations()} className="mt-3 px-4 py-2 bg-rose-600 text-white rounded-lg text-sm hover:bg-rose-700 dark:bg-rose-600 dark:hover:bg-rose-700">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Collaborations</h1>
          <p className="text-sm text-slate-500 mt-0.5 dark:text-slate-500">Manage collaboration requests, negotiations, and active partnerships.</p>
        </div>
        <Link
          to="/admin-marketplace/offers"
          className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700"
        >
          <Store size={16} />
          Browse Offers
        </Link>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm dark:bg-slate-900 dark:border-slate-800">
            <p className="text-sm text-slate-500 dark:text-slate-500">Total</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{summary.total_collaborations}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm dark:bg-slate-900 dark:border-slate-800">
            <p className="text-sm text-slate-500 dark:text-slate-500">Active</p>
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{summary.active_count}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm dark:bg-slate-900 dark:border-slate-800">
            <p className="text-sm text-slate-500 dark:text-slate-500">Pending</p>
            <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{summary.by_status?.pending || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm dark:bg-slate-900 dark:border-slate-800">
            <p className="text-sm text-slate-500 dark:text-slate-500">Awaiting Response</p>
            <p className="text-2xl font-bold text-violet-600 dark:text-violet-400">{summary.awaiting_my_response}</p>
          </div>
        </div>
      )}

      {actionError && (
        <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">
          <AlertCircle size={18} className="mt-0.5 shrink-0" />
          <span className="flex-1">{actionError}</span>
          <button onClick={() => setActionError("")} aria-label="Fermer"><X size={16} /></button>
        </div>
      )}

      {/* Filter */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm dark:bg-slate-900 dark:border-slate-800">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:outline-none dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 dark:focus:ring-indigo-500/30"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="rejected">Rejected</option>
          <option value="countered">Countered</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Collaborations List */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden dark:bg-slate-900 dark:border-slate-800">
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-slate-500 text-sm dark:text-slate-500">No collaborations found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 dark:bg-slate-800/60 dark:border-slate-800">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Offer</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Price</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Kind</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Prestataire</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Created</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filtered.map((collab) => (
                  <tr key={collab.id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900 dark:text-slate-100">{collab.offer_details?.titre || collab.offer}</p>
                    </td>
                    <td className="px-4 py-3 font-medium dark:text-slate-100">{collab.price_finale} {collab.currency}</td>
                    <td className="px-4 py-3"><StatusBadge status={collab.status} /></td>
                    <td className="px-4 py-3 text-slate-600 capitalize dark:text-slate-400">{collab.kind}</td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-700 dark:text-slate-300">{providerName(collab)}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs dark:text-slate-500">{collab.created_at ? new Date(collab.created_at).toLocaleDateString() : "-"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleViewThread(collab.id)}
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 dark:hover:bg-slate-800 dark:text-slate-400"
                          title="View Thread"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() =>
                            navigate(
                              chatLink({
                                type: collab.provider_type,
                                id: collab.provider_id,
                                subject: `Collaboration : ${collab.offer_details?.titre || "offre"}`,
                                collaborationId: collab.id,
                              })
                            )
                          }
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 dark:hover:bg-slate-800 dark:text-slate-400"
                          title="Envoyer un message"
                        >
                          <MessageSquare size={16} />
                        </button>
                        {collab.status === "pending" && (
                          <button
                            onClick={() => setRespondModal({ open: true, collaboration: collab })}
                            disabled={actionLoading === collab.id}
                            className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-600 disabled:opacity-50 dark:hover:bg-indigo-500/10 dark:text-indigo-400"
                            title="Respond"
                          >
                            <Zap size={16} />
                          </button>
                        )}
                        {collab.status === "active" && (
                          <button
                            onClick={() => handleDeactivate(collab.id)}
                            disabled={actionLoading === collab.id}
                            className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 disabled:opacity-50 dark:hover:bg-amber-500/10 dark:text-amber-400"
                            title="Deactivate"
                          >
                            <PowerOff size={16} />
                          </button>
                        )}
                        {collab.status === "inactive" && (
                          <button
                            onClick={() => handleActivate(collab.id)}
                            disabled={actionLoading === collab.id}
                            className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 disabled:opacity-50 dark:hover:bg-emerald-500/10 dark:text-emerald-400"
                            title="Activate"
                          >
                            <Power size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RespondModal
        open={respondModal.open}
        onClose={() => setRespondModal({ open: false, collaboration: null })}
        onRespond={handleRespond}
        collaboration={respondModal.collaboration}
      />
      <ThreadModal
        open={threadModal.open}
        onClose={() => setThreadModal({ open: false, thread: null })}
        thread={threadModal.thread}
      />
    </div>
  );
}
